import React, { useEffect, useMemo } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom } from 'jotai';

import { uiRoutes, useMessages } from '@consumer-frontend/api';
import {
  Container,
  Navbar,
  NavLink,
} from '@consumer-frontend/shared-ui';

import { accountIdAtom, messageDetailPaginationAtom } from '../../store';
import { CnuLoadingSpinner } from '../CnuLoadingSpinner';
import { CnuMessageDetailHeader } from '../CnuMessageDetailHeader';
import { CnuMessageDetailIframe } from '../CnuMessageDetailIframe';
import { MessageDetailPagination } from '../CnuPagination';

import '@consumer-bootstrap/index.scss';
import '@consumer-bootstrap/pages/account-home/message-detail.scss';

export type CnuMessageDetailT = React.FC;

export const CnuMessageDetail: CnuMessageDetailT = () => {
  const [accountId] = useAtom(accountIdAtom);
  const [{ limit, offset }, setPaginationValues] = useAtom(messageDetailPaginationAtom);
  const {
    count: messageCount,
    hasMore,
    messages,
    isLoading: messagesAreLoading,
  } = useMessages({
    id: accountId || '',
    limit,
    offset,
  });

  const message = useMemo(
    () => messages?.[0],
    [messages],
  );

  // Update the detail pagination total value when the messageCount changes.
  useEffect(
    () => {
      setPaginationValues((values) => {
        // Guard against unnecessary re-renders
        if (
          messagesAreLoading
          || (messageCount === values.total && hasMore === values.hasMore)
        ) { return values; }

        return { ...values, hasMore, total: messageCount || 0 };
      });
    },
    [
      hasMore,
      messageCount,
      messagesAreLoading,
      setPaginationValues,
    ],
  );

  // Track page views in Datadog.
  useEffect(
    () => {
      if (messagesAreLoading) return;

      datadogRum.addAction(messageCount ? 'CnuMessageDetailView' : 'CnuMessageDetailEmptyView');
    },
    [messageCount, messagesAreLoading],
  );

  // TEMP PAGINATION HACK: revert once the total count is fixed.
  // if (!message && !messagesAreLoading) {
  //   return <Navigate to={uiRoutes.messages} replace />;
  // }

  return (
    <Container className="message-detail rounded" hasBoxShadow>
      <Navbar className="top-nav">
        <NavLink to={uiRoutes.messages}>
          <span className="ascii-chevron-left" aria-hidden>‹</span>
          <span className="inbox-link-text">Inbox</span>
        </NavLink>
        <MessageDetailPagination />
      </Navbar>

      {/* TEMP PAGINATION HACK: remove once total count is fixed. */}
      {!message && !messagesAreLoading && <p>End of messages.</p>}

      {/* Fetch still in progress */}
      {messagesAreLoading && <CnuLoadingSpinner />}

      {/* Message content */}
      {message && (
        <>
          <CnuMessageDetailHeader
            sender={message.from}
            subject={message.subject}
            timestamp={message.sent_at}
          />

          <hr />

          <CnuMessageDetailIframe html={message.html_part} />

          <hr />

          <Navbar className="bottom-nav">
            <MessageDetailPagination />
          </Navbar>
        </>
      )}

    </Container>
  );
};
